import React, { useEffect, useRef, useState } from "react";

/**
 * NetworkGlobe — premium rotating globe with people-nodes & living connections.
 *
 *  • Pure SVG (no three.js / no canvas), deterministic point distribution.
 *  • Fibonacci-sphere ~64 dots (people) — slow Y-axis rotation.
 *  • Soft latitude meridian ring + dashed equator for "globe" feel.
 *  • Random "active" arcs surge between two nodes (gold pulse) — at any time
 *    3–5 connections are alive, fading in/out with bezier curves.
 *  • Outer halo + inner core gradient → fits SAT Midnight/Violet/Gold palette.
 *  • Respects prefers-reduced-motion (static).
 */

const VIEW = 600;            // SVG viewBox size
const RADIUS = 220;          // sphere radius
const N_POINTS = 72;         // number of people-nodes
const MAX_ARCS = 5;          // simultaneous live connections

const goldenAngle = Math.PI * (3 - Math.sqrt(5));

// Fibonacci-sphere — deterministic, even point distribution
const sphericalPoints = () => {
  const pts = [];
  for (let i = 0; i < N_POINTS; i++) {
    const y = 1 - (i / (N_POINTS - 1)) * 2;       // y in [-1, 1]
    const r = Math.sqrt(1 - y * y);
    const theta = goldenAngle * i;
    pts.push({ x: Math.cos(theta) * r, y, z: Math.sin(theta) * r });
  }
  return pts;
};

const rotateY = (p, a) => {
  const c = Math.cos(a), s = Math.sin(a);
  return { x: p.x * c + p.z * s, y: p.y, z: -p.x * s + p.z * c };
};
const rotateX = (p, a) => {
  const c = Math.cos(a), s = Math.sin(a);
  return { x: p.x, y: p.y * c - p.z * s, z: p.y * s + p.z * c };
};

const project = (p) => {
  // soft perspective — far-back points slightly smaller
  const persp = 1.15 / (1.15 - p.z * 0.45);
  return {
    cx: VIEW / 2 + p.x * RADIUS * persp,
    cy: VIEW / 2 + p.y * RADIUS * persp,
    z: p.z,
    persp,
  };
};

export default function NetworkGlobe({ size = 540, className = "", pulseTick = 0 }) {
  const [angle, setAngle] = useState(0);
  const [arcs, setArcs] = useState([]); // {a:idx, b:idx, t:0..1, life:ms, hero?:bool}
  const basePts = useRef(sphericalPoints());
  const reduced = typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Smooth Y-rotation
  useEffect(() => {
    if (reduced) return;
    let raf, last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      setAngle((a) => a + dt * 0.18);          // ~1 full rotation / 35s
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  // Spawn new arcs periodically
  useEffect(() => {
    if (reduced) return;
    const spawn = () => {
      setArcs((prev) => {
        const cleaned = prev.filter((a) => Date.now() - a.born < a.life);
        if (cleaned.length >= MAX_ARCS) return cleaned;
        const total = basePts.current.length;
        const a = Math.floor(Math.random() * total);
        let b = Math.floor(Math.random() * total);
        if (b === a) b = (b + 1) % total;
        return [
          ...cleaned,
          { a, b, born: Date.now(), life: 2400 + Math.random() * 1800, id: Math.random() },
        ];
      });
    };
    const iv = setInterval(spawn, 750);
    spawn();
    return () => clearInterval(iv);
  }, [reduced]);

  // ── Spawn HERO pulse on external signal (pulseTick changes) ─────
  useEffect(() => {
    if (reduced || pulseTick === 0) return;
    setArcs((prev) => {
      const total = basePts.current.length;
      const a = Math.floor(Math.random() * total);
      let b = Math.floor(Math.random() * total);
      if (b === a) b = (b + 1) % total;
      return [
        ...prev,
        {
          a, b,
          born: Date.now(),
          life: 4200,            // longer-lived than regular arcs
          id: `hero-${pulseTick}`,
          hero: true,             // brighter + thicker
        },
      ];
    });
  }, [pulseTick, reduced]);

  // Compute rotated/projected node positions
  const tiltX = -0.32; // slight axial tilt for cinematic feel
  const proj = basePts.current.map((p) => project(rotateX(rotateY(p, angle), tiltX)));

  // ── render ──
  return (
    <div className={className} style={{ width: size, height: size, position: "relative" }} aria-hidden="true" data-testid="network-globe">
      <svg viewBox={`0 0 ${VIEW} ${VIEW}`} width={size} height={size} style={{ display: "block", overflow: "visible" }}>
        <defs>
          {/* Core gradient — violet → midnight, soft glow inside the sphere */}
          <radialGradient id="ng-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#a48cff" stopOpacity="0.16" />
            <stop offset="55%" stopColor="#4B2E83" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#0E1A30" stopOpacity="0" />
          </radialGradient>
          {/* Outer halo — gold rim */}
          <radialGradient id="ng-halo" cx="50%" cy="50%" r="55%">
            <stop offset="78%" stopColor="#D4AF37" stopOpacity="0" />
            <stop offset="93%" stopColor="#D4AF37" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          {/* Arc gradient — gold pulse */}
          <linearGradient id="ng-arc" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
            <stop offset="50%" stopColor="#EAD08A" stopOpacity="1" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
          <filter id="ng-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.2" />
          </filter>
        </defs>

        {/* Outer halo ring */}
        <circle cx={VIEW / 2} cy={VIEW / 2} r={RADIUS + 14} fill="url(#ng-halo)" />
        {/* Inner core */}
        <circle cx={VIEW / 2} cy={VIEW / 2} r={RADIUS} fill="url(#ng-core)" />
        {/* Subtle outer ring */}
        <circle cx={VIEW / 2} cy={VIEW / 2} r={RADIUS} fill="none"
                stroke="rgba(212,175,55,0.18)" strokeWidth="0.6" />

        {/* Faux meridians (3 ellipses representing longitude lines, rotated with globe) */}
        {[0, 60, 120].map((deg) => {
          const a = angle + (deg * Math.PI) / 180;
          // ellipse minor axis = |cos(rotation)| * R
          const rx = Math.abs(Math.cos(a)) * RADIUS;
          return (
            <ellipse key={deg}
              cx={VIEW / 2} cy={VIEW / 2}
              rx={Math.max(2, rx)} ry={RADIUS}
              fill="none"
              stroke="rgba(164,140,255,0.12)" strokeWidth="0.6" />
          );
        })}
        {/* Equator — dashed */}
        <ellipse cx={VIEW / 2} cy={VIEW / 2} rx={RADIUS} ry={Math.abs(Math.sin(tiltX)) * RADIUS + 1}
                 fill="none" stroke="rgba(212,175,55,0.22)" strokeWidth="0.7" strokeDasharray="3 5" />

        {/* Mesh connections — connect close pairs to form network */}
        <g>
          {proj.map((pa, i) =>
            proj.map((pb, j) => {
              if (j <= i) return null;
              if (pa.z < -0.45 && pb.z < -0.45) return null;          // skip far-back pairs
              const dx = pa.cx - pb.cx, dy = pa.cy - pb.cy;
              const d = Math.sqrt(dx * dx + dy * dy);
              if (d > 110) return null;                                // only close neighbours
              const op = 0.07 + (1 - d / 110) * 0.15;
              const depth = (pa.z + pb.z) / 2;
              return (
                <line key={`${i}-${j}`}
                  x1={pa.cx} y1={pa.cy} x2={pb.cx} y2={pb.cy}
                  stroke="#a48cff"
                  strokeOpacity={Math.max(0.04, op * (0.55 + depth * 0.3))}
                  strokeWidth="0.55" />
              );
            })
          )}
        </g>

        {/* Active surge arcs — gold pulses */}
        <g filter="url(#ng-glow)">
          {arcs.map((arc) => {
            const pa = proj[arc.a], pb = proj[arc.b];
            if (!pa || !pb) return null;
            // skip if both far-back (would render on backside)
            if (pa.z < -0.4 && pb.z < -0.4) return null;
            const t = (Date.now() - arc.born) / arc.life;
            const fade = t < 0.15 ? t / 0.15 : t > 0.7 ? Math.max(0, 1 - (t - 0.7) / 0.3) : 1;
            const mx = (pa.cx + pb.cx) / 2;
            const my = (pa.cy + pb.cy) / 2;
            // bow outward from center
            const cx0 = VIEW / 2, cy0 = VIEW / 2;
            const nx = mx - cx0, ny = my - cy0;
            const nl = Math.sqrt(nx * nx + ny * ny) || 1;
            const bow = 50 * (0.5 + 0.5 * Math.sin(t * Math.PI));
            const ctrlX = mx + (nx / nl) * bow;
            const ctrlY = my + (ny / nl) * bow;
            return (
              <path key={arc.id}
                d={`M ${pa.cx} ${pa.cy} Q ${ctrlX} ${ctrlY} ${pb.cx} ${pb.cy}`}
                fill="none"
                stroke="url(#ng-arc)"
                strokeOpacity={fade * (arc.hero ? 1.3 : 1)}
                strokeWidth={arc.hero ? 2.8 : 1.6}
                strokeLinecap="round" />
            );
          })}
        </g>

        {/* Nodes — people dots */}
        <g>
          {proj.map((p, i) => {
            const r = 1.7 + Math.max(0, p.z) * 2.0;
            const op = 0.45 + Math.max(0, p.z) * 0.55;
            return (
              <g key={i}>
                {p.z > 0.5 && (
                  <circle cx={p.cx} cy={p.cy} r={r * 2.4}
                          fill="#D4AF37" opacity={op * 0.18} />
                )}
                <circle cx={p.cx} cy={p.cy} r={r}
                        fill={p.z > 0.3 ? "#D4AF37" : "#F7F6F2"} opacity={op} />
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
